<script lang="ts" setup>

const props = withDefaults(
  defineProps<{
    images: Array<{ source: string; alt: string; caption?: string }>
    previewMode?: 'contain' | 'cover'
    previewAspectRatio?: number
    loop?: boolean
    lazy?: boolean
  }>(),
  {
    previewMode: 'cover',
    previewAspectRatio: 1,
    loop: true,
    lazy: true,
  },
)

const activeIndex = ref(0)
const show = ref(false)

function openGallery(index: number = 0) {
  activeIndex.value = index
  show.value = true
}

useEventListener('keydown', event => {
  if (!show.value) return

  if (event.key === 'Escape') {
    show.value = false
    event.preventDefault()
    return
  }

  if (event.key === 'ArrowRight') {
    if (props.loop) {
      activeIndex.value = (activeIndex.value + 1) % props.images.length
    } else {
      activeIndex.value = Math.min(activeIndex.value + 1, props.images.length - 1)
    }
    event.preventDefault()
    return
  }

  if (event.key === 'ArrowLeft') {
    if (props.loop) {
      activeIndex.value = (activeIndex.value - 1 + props.images.length) % props.images.length
    } else {
      activeIndex.value = Math.max(activeIndex.value - 1, 0)
    }
    event.preventDefault()
    return
  }
})
</script>

<template>
  <div class="image-grid">
    <button
      v-for="({ source, alt, caption }, index) of images"
      type="button"
      @click="openGallery(index)"
      class="image-grid-item"
    >
      <CroppedImage
        :source="source"
        :alt="alt"
        :lazy="lazy"
        :mode="previewMode"
        :aspect-ratio="previewAspectRatio"
      />
    </button>

    <Galleria
      v-model:activeIndex="activeIndex"
      v-model:visible="show"
      :value="images"
      containerStyle="width: calc(100vw - 2rem); height: calc(100vh - 2rem)"
      :circular="loop"
      :full-screen="true"
      :show-item-navigators="true"
      :show-thumbnails="false"
      :prevItemIcon="'-'"
      :nextItemIcon="'-'"
    >
      <template #item="scope">
        <img
          class="gallery-image"
          :src="scope.item.source"
          :alt="scope.item.alt"
          style="width: 100%; display: block"
        />

        <ProgressSpinner class="gallery-loader" strokeWidth="4" animationDuration="2s" />
      </template>
      <template #caption="scope">
        <div v-if="scope.item.caption" v-html="scope.item.caption"></div>
      </template>
      <template #previousitemicon>
        <svg enable-background="new 0 0 20 20" width="25" height="25" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="m20 9h-16.2l5.6-5.6-1.4-1.4-8 8 8 8 1.4-1.4-5.6-5.6h16.2z" fill="#fff"/></svg>
      </template>
      <template #nextitemicon>
        <svg enable-background="new 0 0 20 20" width="25" height="25" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="m12 2-1.4 1.4 5.6 5.6h-16.2v2h16.2l-5.6 5.6 1.4 1.4 8-8z" fill="#fff"/></svg>
      </template>
      <template #closeicon>
        <svg width="24.0311094px" height="24.0149564px" viewBox="0 0 24.0311094 24.0149564" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
          <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
            <g id="Group" stroke="#ffffff" stroke-width="1.99419192">
              <line x1="0.5" y1="0.5" x2="23.5" y2="23.5" id="Stroke-29"></line>
              <line x1="0.5" y1="23.5" x2="23.5" y2="0.5" id="Stroke-30"></line>
            </g>
          </g>
        </svg>
      </template>
    </Galleria>
  </div>
</template>

<style lang="scss" scoped>
@use '@/styles/util' as *;

.image-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  .image-grid-item {
    @include form-reset;
    cursor: pointer;
    width: var(--gallery-preview-size, 200px);
  }
}

.gallery-loader {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  color: #fff;
}

.gallery-image {
  position: relative;
  z-index: 1;
}

:global(.p-galleria .p-galleria-caption) {
  z-index: 1;
  backdrop-filter: blur(4px);
  background-color: rgba(0 0 0 / 40%);
  width: fit-content;
  max-width: 100%;
  text-wrap: balance;
  right: 0.5rem;
  left: auto;
  border-radius: 0.25rem;
  padding: 0.5rem 1rem;
  bottom: 0.5rem;
  font-size: 0.875rem;
}

:global(.p-galleria .p-galleria-caption a) {
  color: #fff;
}
</style>
